<template>
  <!--
    The touch entry point to the visit checklist. A <span role="button">, not a <button>: it lives
    INSIDE a block (a div) and inside the phone card (already a <button>, which may not contain
    another), and a span is the one thing both can hold. It is made a real control by hand: focusable,
    Enter and Space, and an accessible name that says what it does (its text alone is just "3").
  -->
  <span
    v-if="visible"
    class="sv-clchip"
    :class="[`sv-clchip--${state.tone}`, { 'sv-clchip--big': big }]"
    role="button"
    tabindex="0"
    :aria-label="ariaLabel"
    data-test="sv-checklist-chip"
    @click.stop.prevent="activate"
    @keydown.enter.stop.prevent="activate"
    @keydown.space.stop.prevent="activate"
  >
    <i class="fa" :class="state.tone === 'done' ? 'fa-check' : 'fa-check-square-o'" aria-hidden="true"></i>
    {{ state.label }}
  </span>
</template>

<script>
/**
 * The chip on a block / phone card that opens the visit checklist on a touch device (Phase 10e).
 *
 * Open to-do count, "Done" once every to-do is ticked, or "FYI" when the visit has only notes. Not
 * drawn at all in hover mode (a mouse opens the card by resting on the block) and not drawn for a
 * visit with nothing in its checklist.
 *
 * ⛔ A tap on the chip must NOT also be a tap on the block: tapping the block opens the Patient Panel
 * (Phase 7), and a chip that did both would open the checklist and then immediately bury it under
 * the panel. Every handler therefore stops the event here, and the block never hears of it.
 *
 * The label and tone rule is `chipState()` in utils/scheduleView/checklistLayer.js (pure, node-tested).
 *
 * ⛔ No prop may be named `patNum`, `siteID`, `loading` or `allSettings`: mixin.js defines computeds
 * with those names and a computed SILENTLY REPLACES a same-named prop.
 */
import { chipState, chipAriaLabel } from '@/utils/scheduleView/checklistLayer';

export default {
  name: 'ChecklistChip',

  props: {
    /** { todo, done, fyi } from checklistTotals(). */
    totals: { type: Object, default: () => ({ todo: 0, done: 0, fyi: 0 }) },
    /** How many categories the visit's checklist has; 0 draws nothing. */
    categoryCount: { type: Number, default: 0 },
    /** 'hover' | 'pop' | 'sheet', from the layer. Hover draws no chip. */
    mode: { type: String, default: 'hover' },
    /** The phone card's larger variant. */
    big: { type: Boolean, default: false },
  },

  computed: {
    state() {
      return chipState(this.totals, this.categoryCount);
    },

    visible() {
      return this.mode !== 'hover' && this.state.show;
    },

    ariaLabel() {
      return chipAriaLabel(this.state.left);
    },
  },

  methods: {
    activate() {
      this.$emit('toggle');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-clchip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 7px 0 6px;
  margin-left: auto;
  border-radius: 99px;
  background: $sv-brand-deep;
  color: #ffffff;
  // ⛔ A global link font otherwise wins over a control's own; the card around it is Hanken.
  font-family: inherit;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  flex: 0 0 auto;
  box-shadow: 0 2px 6px -2px rgba(15, 114, 89, 0.6); // $sv-brand at 60%
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  // The visible pill is 20px tall — too small for a thumb. The tap target is inflated around it
  // (the block clips what leaves it, but a block is tall enough for most of it).
  &::after {
    content: '';
    position: absolute;
    top: -12px;
    right: -8px;
    bottom: -12px;
    left: -8px;
  }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: 2px;
  }

  .fa { font-size: 0.95em; }
}

.sv-clchip--done {
  background: $sv-emerald-soft;
  color: $sv-ok-ink;
  box-shadow: none;
}

.sv-clchip--big {
  height: 34px;
  min-width: 52px;
  justify-content: center;
  padding: 0 11px 0 9px;
  gap: 6px;
  font-size: 13px;
  border-radius: 11px;
  margin-left: 0;

  &::after {
    top: -5px;
    right: -5px;
    bottom: -5px;
    left: -5px;
  }
}
</style>
